import { ROUTES, formatPrice } from '@ab-core/functions';
import type { Scaleprice } from '@ab-core/graphql/dist/index';
import AddLineItem from '@ab-core/hooks/cartNew/LineItems/addToCart/addToCart';
import { Icon } from '@ab-core/icon';
import { LINK_VARIANT, Link } from '@ab-core/link';
import useText from '@ab-core/localization/src/useText';
import { cartStore } from '@ab-core/store';
import { getTestIdProp } from '@ab-core/testing';
import Text, { FontTypes } from '@ab-core/text';
import { toast } from '@ab-core/toast';
import type { FC } from 'react';
import React, { useEffect } from 'react';
import { ScalePriceWithIcon } from './styled';

type AddScaleQuantityToCartButtonProps = {
    sku: string;
    positionText?: string;
    scalePrice: Scaleprice;
    cartId?: string;
    disabled?: boolean;
};

const PLACEHOLDER_SPLITT = '$placeholder';

export const AddScaleQuantityToCartButton: FC<AddScaleQuantityToCartButtonProps> = (props) => {
    const { sku, positionText, cartId, scalePrice } = props;
    const productAddedToCartNotification = useText('cartList.productAddedToCartNotification').split(PLACEHOLDER_SPLITT);

    const cartButtonsDisabled = cartStore.getters.useCartButtonDisabled();

    const disableAddScaleToCartClick =
        location.pathname.includes(ROUTES.CART) || location.pathname.includes(ROUTES.CHECKOUT);

    const { addLineItem, success, pending, response, error } = AddLineItem(cartId || '');

    useEffect(() => {
        if (success) {
            response?.upsertLineItemToCarts?.forEach((item) => {
                toast.success(
                    'Warenkorb aktualisiert',
                    <Text
                        type={FontTypes.Subtitle}
                        color="gray70"
                        {...getTestIdProp('toast-success-artikel-hinzugefügt-message')}
                    >
                        {productAddedToCartNotification[0]}
                        <Link variant={LINK_VARIANT.LINK_ONLY} to={`${ROUTES.CART}?${item.id}`}>
                            {
                                item?.custom?.customFieldsRaw?.find(
                                    (customFieldRaw) => customFieldRaw.name === 'cart-name'
                                )?.value
                            }
                        </Link>
                        {productAddedToCartNotification[1]}
                    </Text>
                );
            });

            cartStore.setters.setCartButtonDisabled(false);
        }
    }, [success, response]);

    useEffect(() => {
        if (pending) {
            cartStore.setters.setCartButtonDisabled(true);
        }
    }, [pending]);

    useEffect(() => {
        if (error) {
            toast.error('Fehler beim hinzufügen');
            cartStore.setters.setCartButtonDisabled(false);
        }
    }, [error]);

    const addToCart = () => {
        if (!cartId || cartButtonsDisabled || pending) {
            return;
        }

        const fields = [];

        if (positionText) {
            fields.push({
                name: 'lineitem-positionstext',
                value: positionText
            });
        }

        addLineItem({
            variables: {
                cartIds: cartId,
                lineItemInput: [
                    {
                        sku,
                        fields,
                        id: undefined,
                        quantity: scalePrice.quantity
                    }
                ]
            }
        });
    };

    const getIcon = () => {
        if (pending) {
            return 'Loader';
        }

        return 'Cart';
    };

    const getColor = () => {
        if (pending || cartButtonsDisabled) {
            return 'primaryLightenTwo';
        }

        return 'primary';
    };

    if (disableAddScaleToCartClick) {
        return (
            <Text className="text-right" {...getTestIdProp('scale-price')}>
                {scalePrice.value && formatPrice(scalePrice.value)}
            </Text>
        );
    }

    return (
        <ScalePriceWithIcon
            className="pointer"
            onClick={addToCart}
            {...getTestIdProp('add-scale-price-to-cart-button')}
        >
            <Icon name={getIcon()} color={getColor()} size="SMALL_4" />
            <Text className="text-underline" color={getColor()}>
                {scalePrice.value && formatPrice(scalePrice.value)}
            </Text>
        </ScalePriceWithIcon>
    );
};
